import { useCreateCustomer } from '@/features/customers/hooks/useCustomers';
import { useT } from '@/i18n';
import { CustomerCreateSchema } from '@restaurant/api-contracts';
import {
  Link as F7Link,
  List,
  ListInput,
  NavLeft,
  NavRight,
  NavTitle,
  Navbar,
  Page,
  Preloader,
  f7,
} from 'framework7-react';
import type { Router } from 'framework7/types';
import { useState } from 'react';

// Rule 4 - the form schema IS the contract's create body, reused directly.
const createSchema = CustomerCreateSchema;

type Field = 'firstName' | 'lastName' | 'email' | 'dateOfBirth' | 'notes';

/**
 * Capture a brand-new customer on its own routed page (pushed from the Customers
 * list "+"). Phone is optional but encouraged - identity is phone-first - and is
 * composed into the create via the hook. On success, replaces this page with the
 * new customer's detail so Back returns to the list, not the empty form.
 */
export default function CreateCustomerPage({ f7router }: { f7router: Router.Router }) {
  const t = useT();
  const tc = t.customers.create;
  const create = useCreateCustomer();

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    dateOfBirth: '',
    notes: '',
  });
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});

  const setField = (field: keyof typeof form, value: string) =>
    setForm((f) => ({ ...f, [field]: value }));

  const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });

  const submit = () => {
    if (create.isPending) return;

    // Build the create body, omitting blank optionals (the contract has no
    // nullable-clear - absent means "not set").
    const body: Record<string, string> = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
    };
    if (form.email.trim()) body.email = form.email.trim();
    if (form.dateOfBirth.trim()) body.dateOfBirth = form.dateOfBirth.trim();
    if (form.notes.trim()) body.notes = form.notes.trim();

    // The schema enforces required first/last name + optional-field formats.
    // On failure, surface a per-field message inline AND a toast so the operator
    // both sees what's wrong and knows the tap registered.
    const parsed = createSchema.safeParse(body);
    if (!parsed.success) {
      const next: Partial<Record<Field, string>> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as Field;
        if (!key || next[key]) continue;
        // Friendly operator-facing copy instead of zod's technical default
        // ("String must contain at least 1 character(s)" / "Invalid email").
        if (issue.code === 'too_small') next[key] = tc.requiredError;
        else if (key === 'email') next[key] = tc.invalidEmail;
        else next[key] = issue.message;
      }
      setErrors(next);
      toast(tc.fixErrors);
      return;
    }
    setErrors({});

    create.mutate(
      { ...parsed.data, phone: form.phone },
      {
        onSuccess: ({ customer, phoneError }) => {
          toast(phoneError ? tc.createdPhoneError : tc.created);
          // Replace so Back skips the form and lands on the list. Use the route's
          // own router (f7.views.current is undefined in this nested-view context).
          f7router.navigate(`/customers/${customer.id}`, { reloadCurrent: true });
        },
        onError: () => toast(tc.createFailed),
      },
    );
  };

  return (
    <Page name="create-customer">
      <Navbar>
        <NavLeft>
          <F7Link back>{t.back}</F7Link>
        </NavLeft>
        <NavTitle>{tc.title}</NavTitle>
        <NavRight>
          {create.isPending ? (
            <Preloader size={20} />
          ) : (
            <F7Link onClick={submit}>{tc.submit}</F7Link>
          )}
        </NavRight>
      </Navbar>

      <List strongIos insetIos dividersIos>
        <ListInput
          label={tc.firstName}
          type="text"
          placeholder={tc.required}
          value={form.firstName}
          onInput={(e) => setField('firstName', (e.target as HTMLInputElement).value)}
          errorMessage={errors.firstName}
          errorMessageForce={!!errors.firstName}
          clearButton
        />
        <ListInput
          label={tc.lastName}
          type="text"
          placeholder={tc.required}
          value={form.lastName}
          onInput={(e) => setField('lastName', (e.target as HTMLInputElement).value)}
          errorMessage={errors.lastName}
          errorMessageForce={!!errors.lastName}
          clearButton
        />
        <ListInput
          label={tc.phone}
          type="tel"
          placeholder={tc.optional}
          value={form.phone}
          onInput={(e) => setField('phone', (e.target as HTMLInputElement).value)}
          clearButton
        />
        <ListInput
          label={tc.email}
          type="email"
          placeholder={tc.optional}
          value={form.email}
          onInput={(e) => setField('email', (e.target as HTMLInputElement).value)}
          errorMessage={errors.email}
          errorMessageForce={!!errors.email}
          clearButton
        />
        <ListInput
          label={tc.dateOfBirth}
          type="date"
          placeholder={tc.optional}
          value={form.dateOfBirth}
          onInput={(e) => setField('dateOfBirth', (e.target as HTMLInputElement).value)}
          errorMessage={errors.dateOfBirth}
          errorMessageForce={!!errors.dateOfBirth}
        />
        <ListInput
          label={tc.notes}
          type="textarea"
          placeholder={tc.notesPlaceholder}
          value={form.notes}
          onInput={(e) => setField('notes', (e.target as HTMLTextAreaElement).value)}
          errorMessage={errors.notes}
          errorMessageForce={!!errors.notes}
          resizable
        />
      </List>
    </Page>
  );
}
